/**
 * @fileoverview Agent definition for storyline-finder-v1.
 *
 * Searches provided text (from import source or chapter content) for a
 * specific storyline matching the user's description and priority (A/B/C).
 * Returns the storyline with beats and questions if found, or explicitly
 * reports that no matching storyline exists.
 *
 * Unlike storyline-ideation-v1 (which creates from imagination), this agent
 * extracts from existing text. Unlike the A/B/C extractors (which find ALL
 * storylines), this agent finds ONE specific storyline.
 *
 * @category narrative.story.plot
 * @type primitive
 */

import type { AppAgentDefinition } from '@/domains/agents/schemas/agent';
import { InputSchema, OutputSchema } from './schema';
import { prompt } from './prompt';

export const definition: AppAgentDefinition = {
  id: 'storyline-finder-v1',
  kind: 'primitive',
  name: 'Storyline Finder',
  description: 'Searches provided text for a specific storyline matching the user\'s description and priority. Extracts beats and story questions if found, or reports not found. Designed for targeted single-storyline extraction.',
  category: 'plot',
  defaultPrompt: prompt,
  inputSchema: InputSchema,
  outputSchema: OutputSchema,
  metadata: {
    status: 'active',
    type: 'primitive',
    addedIn: '2026-04',
    lastUpdated: '2026-04',
    taxonomyPath: 'narrative.story.plot',
    usedBy: ['primitives/use-storyline-finder-v1.ts'],
    relatedAgents: [
      'storyline-ideation-v1',
      'a-storyline-extractor-v1',
      'b-storyline-extractor-v1',
      'c-storyline-extractor-v1',
    ],
  },
  recommendedModels: [
    { modelProvider: 'anthropic', modelName: 'claude-opus-4-5' },
    { modelProvider: 'google-ai', modelName: 'gemini-3.1-pro-preview' },
    { modelProvider: 'openai', modelName: 'gpt-5.2' },
  ],
};
